import { formatCurrency, formatNumber, type FormatOptions, formatPercent } from "utils/formatNumber";
import { metricColors } from "components/custom/analytics/palettes";
import { METRIC_GROUPING_LOOKUP } from "./tables/metricsByEntity";
import { metrics as dspMetrics } from "views/dsp/metrics";
import type { ReactNode } from "react";

const IMPRESSIONS_AXIS = 2;
const CLICK_AXIS = 3;
const UNITS_AXIS = 4;
const SERVING_HOURS_AXIS = 5;
const SALES_AXIS = 6;
const PERCENT_AXIS = 7;
const ROI_AXIS = 8;
const BSR_AXIS = 9;
const PRICE_AXIS = 10;
const RATINGS_AXIS = 11;
const RATINGS_TOTAL_AXIS = 12;
const SCORE_AXIS = 13;
const BID_AXIS = 14;
const RATE_AXIS = 15;
const PER_PAGE_VIEW = 16;
const DAYS_AXIS = 17;
const PURCHASES_AXIS = 18;
const CART_ADDS_AXIS = 19;
const VOLUME_AXIS = 20;

export type CompareHighlight = "neutral" | "positiveIsGood" | "negativeIsGood";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface MetricConfig<T = any> {
  /** This is what identifies the metric in various states, e.g are we sorting by this column, are we showing this line on the graph etc. */
  id: string;
  /** Metric label */
  name: string;
  /** Color of the line in the graph */
  color?: string;
  accessor: (row: T | undefined) => number | null;
  prev_accessor?: (row: T | undefined) => number | null;
  totalOverrideAccessor?: string;
  /** @deprecated */
  accessorId?: string;
  format: "date" | "currency" | "percent" | "number" | "text";
  /** @default 2 */
  decimalPlaces?: number;
  chartType?: "line" | "column";
  incremental?: boolean;
  compact?: boolean;
  clipDecimalPlaces?: boolean;
  axis?: number;
  /** AKA description */
  toolTip?: string;
  lower?: boolean;
  overrideValue?: number | null;
  overrideMarketplace?: string;
  /** For column selector */
  grouping?: string;
  actual?: number | null;
  target?: number | null;
  targetFormatter?: (value: unknown) => ReactNode;
  targetLabel?: string;
  options?: object;
  sortNullsLast?: boolean;
  targetColor?: string;
  compareFormatOptions?: FormatOptions;
  /**
   * Determines color of the "compare" part of the metric
   * @default "positiveIsGood"
   */
  compareHighlight?: CompareHighlight;
}

export const metrics: MetricConfig[] = [
  {
    id: "enroll_date",
    name: "Enroll Date",
    color: metricColors.sales[0],
    accessor: (d) => d?.enroll_date,
    accessorId: "enroll_date",
    format: "date",
    axis: SALES_AXIS,
    toolTip: "The date the product was enrolled",
  },
  {
    id: "sales",
    name: "Sales",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales || 0,
    prev_accessor: (d) => d?.total_sales_prev || 0,
    accessorId: "total_sales",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total sales",
    clipDecimalPlaces: false,
  },
  {
    id: "inc_cashflow",
    name: "+Cashflow",
    color: metricColors.sales[0],
    accessor: (d) => d?.inc_cashflow || 0,
    accessorId: "inc_cashflow",
    format: "currency",
    axis: SALES_AXIS,
    chartType: "column",
    toolTip: "The increment cashflow",
  },
  {
    id: "inc_cashflow_prev",
    name: "+ashflow",
    color: metricColors.sales[0],
    accessor: () => undefined,
    accessorId: "inc_cashflow_prev",
    format: "currency",
    axis: SALES_AXIS,
    chartType: "column",
    toolTip: "The increment cashflow",
  },
  {
    id: "inc_units",
    name: "+Units",
    color: metricColors.sales[0],
    accessor: (d) => d?.inc_units || 0,
    accessorId: "inc_units",
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    toolTip: "The increment units",
  },
  {
    id: "inc_units_prev",
    name: "+Units",
    color: metricColors.sales[0],
    accessor: () => undefined,
    accessorId: "inc_units_prev",
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    toolTip: "The increment Units",
  },
  {
    id: "inc_contribution",
    name: "+Contribution",
    color: metricColors.sales[0],
    accessor: (d) => d?.inc_contribution || 0,
    accessorId: "inc_contribution",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The incremental contribution to sales",
  },
  {
    id: "inc_contribution_prev",
    name: "+Contribution",
    color: metricColors.sales[0],
    accessor: () => undefined,
    accessorId: "inc_contribution_prev",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The incremental contribution to sales",
  },

  {
    id: "target_sales",
    name: "Tgt Sales",
    color: metricColors.sales[0],
    accessor: (d) => d?.target_sales,
    accessorId: "target_sales",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total sales",
  },
  {
    id: "target_sales_prev",
    name: "Tgt Sales",
    color: metricColors.sales[0],
    accessor: () => undefined,
    accessorId: "target_sales_prev",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total sales",
  },
  {
    id: "sales_percent",
    name: "Sales %",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "sales_delta",
    name: "Sales (+/-)",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales_delta,
    format: "currency",
    incremental: true,
    axis: SALES_AXIS,
  },
  {
    id: "sales_expcectations",
    name: "Sales Expectations",
    color: metricColors.sales[0],
    accessor: (d) => d?.sales_expcectations,
    accessorId: "sales_expcectations",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "Expected Sales",
  },
  {
    id: "sales_expectations_percent",
    name: "Sales %",
    color: metricColors.sales[0],
    accessor: (d) => d?.sales_expcectations_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "cost",
    name: "Cost",
    color: metricColors.cost[0],
    accessor: (d) => d?.total_cost,
    prev_accessor: (d) => d?.total_cost_prev,
    accessorId: "total_cost",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total costs, including ad spend and discounts",
    compareHighlight: "neutral",
    clipDecimalPlaces: false,
  },
  {
    id: "cost_percent",
    name: "Cost %",
    color: metricColors.cost[0],
    accessor: (d) => d?.total_cost_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "roms",
    name: "ROMS",
    color: metricColors.roms[0],
    accessor: (d) => d?.roms,
    prev_accessor: (d) => d?.roms_prev,
    format: "number",
    chartType: "line",
    decimalPlaces: 2,
    axis: ROI_AXIS,
    compareHighlight: "neutral",
    toolTip:
      "The return on merchandising spend. The total sales divided by the combined ad spend and discounts from deals",
  },
  {
    id: "roms_delta",
    name: "ROMS %",
    color: metricColors.roms[0],
    accessor: (d) => d?.roms_delta,
    format: "number",
    incremental: true,
    chartType: "line",
    decimalPlaces: 2,
    axis: ROI_AXIS,
  },
  {
    id: "units",
    name: "Units",
    color: metricColors.units[0],
    accessor: (d) => d?.total_units || 0,
    prev_accessor: (d) => d?.total_units_prev || 0,
    accessorId: "total_units",
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    toolTip: "The total units sold",
  },
  {
    id: "target_units",
    name: "Tgt Units",
    color: undefined,
    accessor: (d) => d?.target_units,
    accessorId: "target_units",
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
  },
  {
    id: "target_units_prev",
    name: "Tgt Units",
    color: undefined,
    accessor: () => undefined,
    accessorId: "target_units_prev",
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
  },
  {
    id: "total_orders",
    name: "Total Orders",
    color: metricColors.units[0],
    accessor: (d) => d?.total_order_count,
    prev_accessor: (d) => d?.total_order_count_prev,
    accessorId: "total_order_count",
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    toolTip: "The total number of orders",
  },
  {
    id: "total_order_count_percent",
    name: "Total Orders %",
    color: metricColors.units[0],
    accessor: (d) => d?.total_order_count_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "units_expectations",
    name: "Units Expectations",
    color: metricColors.units[0],
    accessor: (d) => d?.units_expectations,
    accessorId: "units_expectations",
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    toolTip: "The total expected units",
  },
  {
    id: "units_percent",
    name: "Units %",
    color: metricColors.units[0],
    accessor: (d) => d?.total_units_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "units_delta",
    name: "Units (+/-)",
    color: metricColors.units[0],
    accessor: (d) => d?.total_units_delta,
    format: "number",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "units_expectations_percent",
    name: "Units %",
    color: metricColors.units[0],
    accessor: (d) => d?.units_expectations_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "margin",
    name: "Margin",
    color: metricColors.margin[0],
    accessor: (d) => d?.margin,
    prev_accessor: (d) => d?.margin_prev,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of profit after all costs",
    decimalPlaces: 1,
  },
  {
    id: "margin_delta",
    name: "Margin %",
    color: metricColors.margin[0],
    accessor: (d) => d?.margin_delta,
    format: "percent",
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
    decimalPlaces: 1,
  },
  {
    id: "margin_wo_adspend",
    name: "Margin w/o Ad Spend",
    color: metricColors.margin[0],
    accessor: (d) => d?.margin_wo_adspend,
    prev_accessor: (d) => d?.margin_prev_prev,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of profit after all COGS",
    decimalPlaces: 1,
  },
  {
    id: "target_margin",
    name: "Target Margin",
    color: metricColors.margin[0],
    accessor: (d) => d?.target_margin,
    prev_accessor: (d) => d?.target_margin,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of profit after all COGS",
  },
  {
    id: "margin_wo_adspend_delta",
    name: "Margin w/o Ad Spend %",
    color: metricColors.margin[0],
    accessor: (d) => d?.margin_wo_adspend_delta,
    format: "percent",
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
    decimalPlaces: 1,
  },
  {
    id: "margin_expcectations",
    name: "Margin Expectations",
    color: metricColors.sales[0],
    accessor: (d) => d?.margin_expcectations,
    accessorId: "margin_expcectations",
    format: "percent",
    axis: SALES_AXIS,
    toolTip: "Expected Sales",
  },
  {
    id: "margin_expectations_percent",
    name: "Margin %",
    color: metricColors.sales[0],
    accessor: (d) => d?.margin_expcectations_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "ad_sales",
    name: "Ad Sales",
    color: metricColors.sales[4],
    accessor: (d) => d?.ad_sales ?? 0,
    prev_accessor: (d) => d?.ad_sales_prev ?? 0,
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total sales from ads",
    clipDecimalPlaces: false,
  },
  {
    id: "ad_sales_percent",
    name: "Ad Sales %",
    color: metricColors.sales[4],
    accessor: (d) => d?.ad_sales_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "ad_sales_same_sku",
    name: "Same SKU Ad Sales",
    color: metricColors.sales[4],
    accessor: (d) => d?.ad_sales_same_sku ?? 0,
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total sales from ads",
    clipDecimalPlaces: false,
  },
  {
    id: "ad_sales_same_sku_percent",
    name: "Same SKU Ad Sales %",
    color: metricColors.sales[4],
    accessor: (d) => d?.ad_sales_same_sku_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "ad_spend",
    name: "Ad Spend",
    color: metricColors.cost[1],
    accessor: (d) => d?.cost ?? 0,
    prev_accessor: (d) => d?.cost_prev ?? 0,
    accessorId: "cost",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total ad spend",
    compareHighlight: "neutral",
    clipDecimalPlaces: false,
  },
  {
    id: "ad_spend_percent",
    name: "Ad Spend %",
    color: metricColors.cost[1],
    accessor: (d) => d?.cost_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "clicks",
    name: "Clicks",
    color: metricColors.metric1[0],
    accessor: (d) => d?.clicks ?? 0,
    prev_accessor: (d) => d?.clicks_prev ?? 0,
    chartType: "column",
    format: "number",
    axis: CLICK_AXIS,
    toolTip: "The total clicks on ads",
  },
  {
    id: "clicks_percent",
    name: "Clicks %",
    color: metricColors.metric1[0],
    accessor: (d) => d?.clicks_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "troas",
    name: "TROAS",
    color: metricColors.roms[3],
    accessor: (d) => d?.total_roas ?? 0,
    prev_accessor: (d) => d?.total_roas_prev ?? 0,
    accessorId: "total_roas",
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    toolTip: "The return on ad spend against total sales",
  },
  {
    id: "troas_delta",
    name: "TROAS Change",
    color: metricColors.roms[3],
    accessor: (d) => d?.total_roas_delta,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    incremental: true,
  },
  {
    id: "roas",
    name: "ROAS",
    color: metricColors.roms[1],
    accessor: (d) => d?.roas ?? 0,
    prev_accessor: (d) => d?.roas_prev ?? 0,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    toolTip: "The return on ad spend",
  },
  {
    id: "roas_delta",
    name: "ROAS Change",
    color: metricColors.roms[1],
    accessor: (d) => d?.roas_delta,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    incremental: true,
  },
  {
    id: "roas_same_sku",
    name: "Same SKU ROAS",
    color: metricColors.roms[1],
    accessor: (d) => d?.roas_same_sku ?? 0,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    toolTip: "The return on ad spend",
  },
  {
    id: "roas_same_sku_delta",
    name: "Same SKU ROAS Change",
    color: metricColors.roms[1],
    accessor: (d) => d?.roas_same_sku_delta,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    incremental: true,
  },
  {
    id: "total_acos",
    name: "Total ACOS",
    color: metricColors.acos[1],
    accessor: (d) => d?.total_acos ?? 0,
    prev_accessor: (d) => d?.total_acos_prev ?? 0,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The return on ad spend",
    lower: true,
    compareHighlight: "negativeIsGood",
    decimalPlaces: 1,
  },
  {
    id: "total_acos_delta",
    name: "Total ACOS Change",
    color: metricColors.acos[1],
    accessor: (d) => d?.total_acos_delta,
    format: "percent",
    decimalPlaces: 1,
    chartType: "line",
    axis: PERCENT_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "acos",
    name: "ACOS",
    color: metricColors.acos[3],
    accessor: (d) => d?.acos ?? 0,
    prev_accessor: (d) => d?.acos_prev ?? 0,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The return on ad spend",
    lower: true,
    compareHighlight: "negativeIsGood",
    decimalPlaces: 1,
  },
  {
    id: "acos_delta",
    name: "ACOS Change",
    color: metricColors.acos[3],
    accessor: (d) => d?.acos_delta,
    format: "percent",
    decimalPlaces: 1,
    chartType: "line",
    axis: PERCENT_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "acos_same_sku",
    name: "Same SKU ACOS",
    color: undefined,
    accessor: (d) => d?.acos_same_sku ?? 0,
    prev_accessor: (d) => d?.acos_same_sku_prev ?? 0,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The return on ad spend",
    lower: true,
    compareHighlight: "negativeIsGood",
  },
  {
    id: "acos_same_sku_delta",
    name: "Same SKU ACOS Change",
    color: undefined,
    accessor: (d) => d?.acos_same_sku_delta,
    format: "percent",
    decimalPlaces: 1,
    chartType: "line",
    axis: PERCENT_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "profit",
    name: "Profit",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit,
    prev_accessor: (d) => d?.profit_prev,
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total profit after all costs",
  },
  {
    id: "profit_percent",
    name: "Profit %",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "profit_delta",
    name: "Profit (+/-)",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit_delta,
    format: "currency",
    axis: SALES_AXIS,
    incremental: true,
  },
  {
    id: "profit_wo_adspend",
    name: "Profit w/o Ad Spend",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit_wo_adspend,
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total profit after all costs",
  },
  {
    id: "profit_wo_adspend_percent",
    name: "Profit w/o Ad Spend %",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit_wo_adspend_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "profit_wo_adspend_delta",
    name: "Profit w/o Ad SPend (+/-)",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit_wo_adspend_delta,
    format: "currency",
    axis: SALES_AXIS,
    incremental: true,
  },

  {
    id: "profit_expcectations",
    name: "Profit Expectations",
    color: metricColors.sales[0],
    accessor: (d) => d?.profit_expcectations,
    accessorId: "sales_expcectations",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "Expected Profit",
  },
  {
    id: "profit_expectations_percent",
    name: "Profit %",
    color: metricColors.sales[0],
    accessor: (d) => d?.profit_expcectations_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "price",
    name: "Price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.product_price,
    prev_accessor: (d) => d?.product_price_prev,
    accessorId: "product_price",
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    toolTip: "The price of the product",
  },
  {
    id: "reference_price",
    name: "Price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.reference_price,
    accessorId: "reference_price",
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    toolTip: "The reference price of the product",
  },
  {
    id: "min_price",
    name: "Min Price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.min_price,
    prev_accessor: (d) => d?.product_price,
    accessorId: "min_pirce",
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    toolTip: "The minimum price of the product",
  },
  {
    id: "max_price",
    name: "Max Price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.max_price,
    prev_accessor: (d) => d?.product_price,
    accessorId: "max_price",
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    toolTip: "The minimum price of the product",
  },
  {
    id: "suggested_price",
    name: "Suggested",
    color: metricColors.metric2[0],
    accessor: (d) => d?.suggested_price,
    accessorId: "suggested_price",
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    toolTip: "The suggested price of the product",
  },
  {
    id: "product_price_prev",
    name: "Previous Price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.product_price_prev,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    toolTip: "The previous price of the product",
  },
  {
    id: "total_conv_rate",
    name: "Total Conv/Rate",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_conv_rate,
    prev_accessor: (d) => d?.total_conv_rate_prev,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of page views that result in a sale.",
    decimalPlaces: 2,
  },
  {
    id: "total_conv_rate_delta",
    name: "Total Conv/Rate Change",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_conv_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "total_conv_rate_prev",
    name: "Previous Total Conversion Rate",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_conv_rate_prev,
    format: "percent",
  },
  {
    id: "organic_sales",
    name: "Organic Sales",
    color: metricColors.sales[0],
    accessor: (d) => d?.organic_sales,
    prev_accessor: (d) => d?.organic_sales_prev,
    axis: SALES_AXIS,
    format: "currency",
    toolTip: "The amount of sales attained organically",
  },
  {
    id: "organic_sales_delta",
    name: "Organic Sales Change",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_sales_delta,
    format: "percent",
  },
  {
    id: "organic_sales_prev",
    name: "Previous Organic Sales",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_sales_prev,
    format: "currency",
  },
  {
    id: "organic_sales_percent",
    name: "Organic Sales Percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_sales_percent,
    format: "percent",
  },
  {
    id: "organic_units",
    name: "Organic Units",
    color: metricColors.units[0],
    accessor: (d) => d?.organic_units,
    prev_accessor: (d) => d?.organic_units_prev,
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    toolTip: "The amount of units sold organically,",
  },
  {
    id: "organic_units_delta",
    name: "Organic Units Sales",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_unit_delta,
    format: "percent",
  },
  {
    id: "organic_units_percent",
    name: "Percent Change Organic Units",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_units_percent,
    format: "percent",
  },
  {
    id: "organic_units_prev",
    name: "Previous Organic Units",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_units_prev,
    format: "number",
  },
  {
    id: "organic_page_views",
    name: "Organic Page Views",
    color: metricColors.metric2[0],
    accessor: (d) => d?.organic_page_views,
    prev_accessor: (d) => d?.organic_page_views_prev,
    format: "number",
    toolTip: "The amount of page views attained organically",
  },
  {
    id: "organic_page_views_delta",
    name: "Organic Page Views Change",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_page_views_delta,
    format: "percent",
  },
  {
    id: "organic_page_views_prev",
    name: "Previous Organic Page Views",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_page_views_prev,
    format: "number",
  },
  {
    id: "organic_page_views_percent",
    name: "Percent Change Organic Page Views",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_page_views_percent,
    format: "percent",
  },
  {
    id: "org_conv_rate",
    name: "Org Conv/Rate",
    color: metricColors.metric1[0],
    accessor: (d) => d?.org_conv_rate,
    prev_accessor: (d) => d?.org_conv_rate_prev,
    format: "percent",
    toolTip: "The Conversion Rate obtained Organically",
  },
  {
    id: "org_conv_rate_delta",
    name: "Org Conv/Rate Change",
    color: metricColors.metric1[0],
    accessor: (d) => d?.org_conv_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "org_conv_rate_prev",
    name: "Previous Org Conv/Rate",
    color: metricColors.metric1[0],
    accessor: (d) => d?.org_conv_rate_prev,
    format: "percent",
  },
  {
    id: "price_delta",
    name: "Price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.product_price_delta,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    incremental: true,
  },
  {
    id: "bsr",
    name: "BSR",
    toolTip: "Sales rank, the lower is better",
    color: metricColors.metric3[0],
    accessor: (d) => d?.bsr_rank,
    prev_accessor: (d) => d?.bsr_rank_prev,
    accessorId: "bsr_rank",
    totalOverrideAccessor: "bsr_delta",
    format: "number",
    chartType: "line",
    axis: BSR_AXIS,
    lower: true,
    sortNullsLast: true,
    compareHighlight: "negativeIsGood",
  },
  {
    id: "bsr_percent",
    name: "BSR %",
    color: metricColors.metric3[0],
    accessor: (d) => d?.bsr_rank_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "bsr_delta",
    name: "BSR Delta",
    color: metricColors.metric3[0],
    accessor: (d) => d?.bsr_rank_delta,
    format: "number",
    axis: BSR_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "promo_sales",
    toolTip: "Total Sales from Deals",
    name: "Deals Sales",
    color: metricColors.sales[2],
    accessor: (d) => d?.promo_sales,
    prev_accessor: (d) => d?.promo_sales_prev,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "promo_sales_percent",
    toolTip: "Total Sales from Deals percentage",
    name: "Deals Sales %",
    color: metricColors.sales[2],
    accessor: (d) => d?.promo_sales_percent,
    axis: PERCENT_AXIS,
    format: "percent",
    chartType: "line",
    incremental: true,
  },
  {
    id: "promo_units",
    toolTip: "Total Units sold from Deals",
    name: "Deals Units",
    color: metricColors.units[1],
    accessor: (d) => d?.promo_units,
    prev_accessor: (d) => d?.promo_units_prev,
    format: "number",
    axis: UNITS_AXIS,
  },
  {
    id: "promo_units_percent",
    toolTip: "Total Units sold from Deals percentage",
    name: "Deals Units %",
    color: metricColors.units[1],
    accessor: (d) => d?.promo_units_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    chartType: "line",
    incremental: true,
  },
  {
    id: "rods",
    toolTip: "Return on deal spend",
    name: "RODS",
    color: metricColors.roms[2],
    accessor: (d) => d?.rods,
    axis: ROI_AXIS,
    format: "number",
    chartType: "line",
  },
  {
    id: "rods_delta",
    toolTip: "Change in Return on deal spend",
    name: "RODS Change",
    color: metricColors.roms[2],
    accessor: (d) => d?.rods_delta,
    format: "number",
    axis: ROI_AXIS,
    incremental: true,
  },
  {
    id: "promo_conversion_rate",
    toolTip: "The percentage of glance views that results in a deal sale",
    name: "Deals Conversion Rate",
    color: metricColors.metric1[2],
    accessor: (d) => d?.conv,
    accessorId: "conv",
    axis: PERCENT_AXIS,
    format: "percent",
    chartType: "line",
  },
  {
    id: "promo_conversion_rate_delta",
    toolTip: "",
    name: "Deals Conversion Rate Change",
    color: metricColors.metric1[2],
    accessor: (d) => d?.conv_delta,
    prev_accessor: (d) => d?.conv_delta_prev,
    axis: PERCENT_AXIS,
    format: "percent",
    chartType: "line",
  },
  {
    id: "promo_cost",
    toolTip: "Total amount of discounts applied",
    name: "Total Discounts",
    color: metricColors.cost[2],
    accessor: (d) => d?.promo_cost,
    prev_accessor: (d) => d?.promo_cost_prev,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "promo_cost_percent",
    name: "Total Discounts %",
    color: metricColors.cost[2],
    accessor: (d) => d?.promo_cost_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "cogs",
    toolTip: "Total Cost of Goods Sold",
    name: "COGS",
    color: metricColors.metric3[0],
    accessor: (d) => d?.total_cogs,
    prev_accessor: (d) => d?.total_cogs_prev,
    accessorId: "total_cogs",
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "cogs_percent",
    name: "COGS %",
    color: metricColors.metric3[0],
    accessor: (d) => d?.total_cogs_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "last_bsr_rank",
    name: "Last BSR",
    toolTip: "The most recent sales rank, the lower is better",
    color: metricColors.metric3[0],
    accessor: (d) => d?.last_bsr_rank,
    prev_accessor: (d) => d?.last_bsr_rank_delta,
    format: "number",
    chartType: "line",
    axis: BSR_AXIS,
    compareHighlight: "negativeIsGood",
  },
  {
    id: "last_bsr_rank_delta",
    name: "Last Best Seller Rank Change",
    toolTip: "The most recent sales rank, the lower is better",
    color: metricColors.metric3[0],
    accessor: (d) => d?.last_bsr_rank_delta,
    format: "number",
    chartType: "line",
    axis: BSR_AXIS,
  },
  {
    id: "bsr_percent",
    name: "Best Seller Rank %",
    color: metricColors.metric3[0],
    accessor: (d) => d?.bsr_rank_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "rating",
    name: "Rating",
    toolTip: "Average rating across all enrolled products",
    color: metricColors.metric4[0],
    accessor: (d) => d?.rating,
    totalOverrideAccessor: "last_rating",
    format: "number",
    chartType: "line",
    axis: RATINGS_AXIS,
  },
  {
    id: "last_rating",
    name: "Last Rating",
    toolTip: "The most recent average rating across all enrolled products",
    color: metricColors.metric4[0],
    accessor: (d) => d?.last_rating,
    format: "number",
    chartType: "line",
    axis: RATINGS_AXIS,
  },
  {
    id: "rating_percent",
    name: "Ratings %",
    color: metricColors.metric4[0],
    accessor: (d) => d?.last_rating_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "ratings_total",
    name: "# of Ratings",
    toolTip: "Number of ratings across all enrolled products",
    color: metricColors.metric4[4],
    accessor: (d) => d?.ratings_total,
    totalOverrideAccessor: "last_ratings_total",
    format: "number",
    chartType: "line",
    axis: RATINGS_TOTAL_AXIS,
  },
  {
    id: "last_ratings_total",
    name: "Last # of Ratings",
    toolTip: "The most recent number of ratings across all enrolled products",
    color: metricColors.metric4[0],
    accessor: (d) => d?.last_ratings_total,
    format: "number",
    chartType: "line",
    axis: RATINGS_TOTAL_AXIS,
  },
  {
    id: "ratings_total_percent",
    name: "# of Ratings %",
    color: metricColors.metric4[0],
    accessor: (d) => d?.ratings_total_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "seo_score",
    name: "Searchability",
    color: metricColors.sales[4],
    accessor: (d) => d?.seo_score,
    format: "number",
    axis: SCORE_AXIS,
    decimalPlaces: 0,
    toolTip:
      "This is a reflection of how your product will rank organically on Amazon search results and the chance of getting clicks. We take into account the title, image, price, rating and more. This score is out of 100.",
  },
  {
    id: "seo_score_percent",
    name: "Searchability %",
    color: metricColors.sales[4],
    accessor: (d) => d?.seo_score_percent,
    format: "percent",
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
  },
  {
    id: "buyability_score",
    name: "Buyability",
    color: metricColors.metric1[4],
    decimalPlaces: 0,
    accessor: (d) => d?.buyability_score,
    format: "number",
    axis: SCORE_AXIS,
    toolTip:
      "This determines how likely your product is to convert when a customer is viewing your listing. We take into account the quality of your bullets points, images, ratings, reviews, and more. This score is out of 100.",
  },
  {
    id: "buyability_score_percent",
    name: "Buyability Score %",
    color: metricColors.metric1[4],
    accessor: (d) => d?.buyability_score_percent,
    format: "percent",
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
  },
  {
    id: "conversion_rate",
    name: "Ad Conversion Rate",
    color: metricColors.metric2[4],
    accessor: (d) => d?.conv_rate ?? 0,
    prev_accessor: (d) => d?.conv_rate_prev ?? 0,
    accessorId: "conv_rate",
    format: "percent",
    chartType: "line",
    axis: RATE_AXIS,
    toolTip: "The percentage of advertising clicks that result in a sale.",
    decimalPlaces: 2,
  },
  {
    id: "conversion_rate_delta",
    name: "Conversion Rate Change",
    color: metricColors.metric2[4],
    accessor: (d) => d?.conv_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "ctr",
    name: "CTR",
    color: metricColors.metric3[4],
    accessor: (d) => d?.click_thru_rate,
    prev_accessor: (d) => d?.click_thru_rate_prev,
    accessorId: "click_thru_rate",
    format: "percent",
    chartType: "line",
    axis: RATE_AXIS,
    toolTip: "The percentage of impressions that result in a click.",
    decimalPlaces: 2,
  },
  {
    id: "ctr_delta",
    name: "CTR Change",
    color: metricColors.metric3[4],
    accessor: (d) => d?.click_thru_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "cpc",
    name: "CPC",
    color: metricColors.metric4[4],
    accessor: (d) => d?.cost_per_click ?? 0,
    prev_accessor: (d) => d?.cost_per_click_prev ?? 0,
    accessorId: "cost_per_click",
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    toolTip: "The average cost per click.",
    decimalPlaces: 2,
    lower: true,
    compareHighlight: "negativeIsGood",
  },
  {
    id: "cpc_delta",
    name: "CPC Change",
    color: metricColors.metric4[4],
    accessor: (d) => d?.cost_per_click_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "impressions",
    name: "Impressions",
    color: metricColors.metric1[3],
    accessor: (d) => d?.impressions ?? 0,
    prev_accessor: (d) => d?.impressions_prev ?? 0,
    format: "number",
    chartType: "column",
    axis: IMPRESSIONS_AXIS,
    toolTip: "Advertising impressions",
  },
  {
    id: "viewable_impressions",
    name: "Impression Views",
    color: metricColors.metric1[3],
    accessor: (d) => d?.viewable_impressions ?? 0,
    prev_accessor: (d) => d?.viewable_impressions_prev ?? 0,
    format: "number",
    chartType: "line",
    axis: IMPRESSIONS_AXIS,
    toolTip: "Advertising impression views",
  },
  {
    id: "viewable_impressions_percent",
    name: "Viewable Impressions %",
    color: metricColors.metric1[3],
    accessor: (d) => d?.viewable_impressions_percent ?? 0,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "viewability_rate",
    name: "Viewability Rate",
    color: metricColors.metric1[3],
    accessor: (d) => d?.viewability_rate ?? 0,
    prev_accessor: (d) => d?.viewability_rate_prev ?? 0,
    format: "percent",
    axis: PERCENT_AXIS,
    chartType: "line",
    toolTip: "View-Through Rate (Views / Impressions)",
  },
  {
    id: "viewability_rate_delta",
    name: "Vewability Rate %",
    color: metricColors.metric1[3],
    accessor: (d) => d?.viewability_rate_delta ?? 0,
    format: "percent",
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
  },
  {
    id: "view_click_through_rate",
    name: "View Click Through Rate",
    color: metricColors.metric1[3],
    accessor: (d) => d?.view_click_through_rate ?? 0,
    prev_accessor: (d) => d?.view_click_through_rate_prev ?? 0,
    format: "percent",
    axis: PERCENT_AXIS,
    chartType: "line",
    toolTip: "Click-Through Rate for Views (Clicks / Views)",
  },
  {
    id: "view_click_through_rate_delta",
    name: "View Click Through Rate %",
    color: metricColors.metric1[3],
    accessor: (d) => d?.view_click_through_rate_delta ?? 0,
    format: "percent",
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
  },
  {
    id: "impressions_percent",
    name: "Impressions %",
    color: metricColors.metric1[3],
    accessor: (d) => d?.impressions_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "asp",
    name: "ASP",
    color: metricColors.metric2[0],
    accessor: (d) => (d?.total_units > 0 ? d?.total_sales / d?.total_units : 0),
    // prev_accessor: (d) => (d?.total_units_prev > 0 ? d?.total_sales_prev / d?.total_units_prev : 0),
    format: "currency",
    axis: PRICE_AXIS,
    toolTip: "Average Selling Price",
  },
  {
    id: "inc_price",
    name: "Inc. Price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.inc_price,
    format: "currency",
    axis: PRICE_AXIS,
    toolTip: "Incremental Price",
  },

  {
    id: "page_views",
    name: "Page Views",
    color: metricColors.metric2[0],
    accessor: (d) => d?.page_views,
    prev_accessor: (d) => d?.page_views_prev,
    format: "number",
    axis: IMPRESSIONS_AXIS,
    toolTip: "Total page views",
  },
  {
    id: "page_views_percent",
    name: "Page Views Percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.page_views_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "Total page views percentage",
  },
  {
    id: "match_type",
    name: "Match Type",
    color: metricColors.metric2[0],
    accessor: (d) => d?.match_type,
    format: "text",
    axis: IMPRESSIONS_AXIS,
    toolTip: "Target match type",
  },
  {
    id: "bid",
    name: "Current Bid",
    color: metricColors.sales[0],
    accessor: (d) => d?.bid,
    prev_accessor: (d) => d?.bid_prev,
    format: "currency",
    axis: BID_AXIS,
    toolTip: "Current keyword bid",
  },
  {
    id: "daily_bid",
    name: "Bid",
    color: metricColors.metric2[0],
    accessor: (d) => d?.daily_bid ?? 0,
    prev_accessor: (d) => d?.daily_bid_prev ?? 0,
    format: "currency",
    axis: PRICE_AXIS,
    toolTip: "Current keyword bid",
  },
  {
    id: "target_source",
    name: "Source",
    color: metricColors.metric4[0],
    accessor: (d) => d?.target_source,
    chartType: "column",
    format: "text",
    axis: CLICK_AXIS,
    toolTip: "The source of the advertisement target.",
  },
  {
    id: "orders",
    name: "Conversions",
    color: metricColors.metric4[4],
    accessor: (d) => d?.conversion ?? 0,
    prev_accessor: (d) => d?.conversion_prev ?? 0,
    accessorId: "conversion",
    chartType: "column",
    format: "number",
    axis: UNITS_AXIS,
    toolTip: "The total number of conversions",
  },
  {
    id: "conversion_percent",
    name: "Conversions Percent",
    color: metricColors.metric4[4],
    accessor: (d) => d?.conversion_percent ?? 0,
    accessorId: "conversion_percent",
    chartType: "column",
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "orders_percent",
    name: "Orders %",
    color: metricColors.metric4[4],
    accessor: (d) => d?.orders_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "orders_same_sku",
    name: "Same SKU Orders",
    color: metricColors.metric4[4],
    accessor: (d) => d?.conversion_same_sku,
    accessorId: "conversion_same_sku",
    chartType: "column",
    format: "number",
    axis: UNITS_AXIS,
    toolTip: "The total number of orders",
  },
  {
    id: "orders_same_sku_percent",
    name: "Same SKU Orders %",
    color: metricColors.metric4[4],
    accessor: (d) => d?.orders_same_sku_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "user_status",
    name: "Active",
    color: metricColors.metric4[0],
    accessor: (d) => d?.user_status,
    format: "text",
    axis: PERCENT_AXIS,
  },
  {
    id: "stock_status",
    name: "Stock Status",
    color: metricColors.metric4[0],
    accessor: (d) => d?.stock_status,
    format: "text",
    axis: PERCENT_AXIS,
  },
  {
    id: "serving_status",
    name: "Serving Status",
    color: metricColors.metric4[0],
    accessor: (d) => d?.serving_status,
    format: "text",
    axis: PERCENT_AXIS,
  },
  {
    // TODO: Tooltip might not be accurate description, don't merge until confirming
    id: "profit_per_page_view",
    name: "PPV",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view,
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The average profit per page view driven by advertisement",
  },
  {
    id: "profit_per_page_view_delta",
    name: "(+/-) Profit / Session",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view_delta,
    prev_accessor: (d) => d?.profit_per_page_view_delta_prev,
    format: "currency",
    axis: PER_PAGE_VIEW,
  },
  {
    id: "profit_per_page_view_percent",
    name: "Profit Per Session Percent Change",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  // TODO: Tooltip might not be accurate description, don't merge until confirming
  {
    id: "profit_per_page_view_wo_adspend",
    name: "Organic PPV",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view_wo_adspend,
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The average profit per page view",
  },
  {
    id: "profit_per_page_view_wo_adspend_delta",
    name: "Org PPV Delta",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view_wo_adspend_delta,
    format: "currency",
    axis: PER_PAGE_VIEW,
  },
  {
    id: "profit_per_page_view_wo_adspend_percent",
    name: "Org PPV Percent Change",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view_wo_adspend_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "boosted_profit",
    name: "Incremental Profit",
    color: metricColors.metric4[0],
    accessor: (d) => d?.boosted_profit,
    format: "currency",
    axis: PRICE_AXIS,
  },
  {
    id: "serving_hours",
    name: "Serving Hours",
    color: metricColors.metric2[0],
    accessor: (d) => d?.serving_hours,
    prev_accessor: (d) => d?.serving_hours_prev ?? 0,
    format: "number",
    axis: SERVING_HOURS_AXIS,
    chartType: "column",
    toolTip: "The number of hours that media plan was serving",
    clipDecimalPlaces: true,
  },
  {
    id: "serving_hours_delta",
    name: "Serving Hours Delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.serving_hours_delta,
    format: "number",
    axis: SERVING_HOURS_AXIS,
    clipDecimalPlaces: true,
  },
  {
    id: "budget_goal_progression",
    name: "Cumulative Budget Allocation",
    color: metricColors.sales[4],
    accessor: (d) => d?.budget_goal_progression ?? 0,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "active_ad_type_count",
    name: "Ad Type Count",
    color: metricColors.metric2[1],
    accessor: (d) => d?.active_ad_type_count,
    format: "number",
    axis: SERVING_HOURS_AXIS,
  },
  {
    id: "active_ad_type_count_percent",
    name: "Ad Type Count &",
    color: metricColors.sales[4],
    accessor: (d) => d?.active_ad_type_count_percent ?? 0,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "orders_repeat",
    name: "Repeat Orders",
    color: metricColors.repeat[1],
    accessor: (d) => d?.orders_repeat,
    format: "number",
    chartType: "column",
    axis: UNITS_AXIS,
  },
  {
    id: "orders_ntb",
    name: "New to Brand Orders",
    color: metricColors.ntb[1],
    accessor: (d) => d?.orders_ntb,
    format: "number",
    chartType: "column",
    axis: UNITS_AXIS,
  },
  {
    id: "sales_repeat",
    name: "Repeat Sales",
    color: metricColors.repeat[0],
    accessor: (d) => d?.sales_repeat,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "sales_ntb",
    name: "New to Brand Sales",
    color: metricColors.ntb[0],
    accessor: (d) => d?.sales_ntb,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "customer_value",
    name: "Lifetime Value",
    color: metricColors.sales[4],
    accessor: (d) => d?.customer_value,
    format: "currency",
    axis: PRICE_AXIS,
  },
  {
    id: "brand_customer_value",
    name: "Brand Lifetime Value",
    color: metricColors.sales[3],
    accessor: (d) => d?.brand_customer_value,
    format: "currency",
    axis: PRICE_AXIS,
  },
  {
    id: "cac",
    name: "Customer Acquisition Cost",
    color: metricColors.cost[1],
    accessor: (d) => d?.cac,
    format: "currency",
    axis: PRICE_AXIS,
  },
  {
    id: "ltv_cac",
    name: "LTV:CAC Ratio",
    color: metricColors.sales[4],
    accessor: (d) => d?.ltv_cac,
    format: "number",
    axis: ROI_AXIS,
  },
  {
    id: "purchase_freq",
    name: "Repurchase Frequency",
    color: metricColors.metric1[1],
    accessor: (d) => d?.purchase_freq,
    format: "number",
    axis: DAYS_AXIS,
  },
  {
    id: "halo_rank",
    name: "Halo Rank",
    color: metricColors.metric1[1],
    accessor: (d) => d?.halo_rank,
    format: "number",
    axis: BSR_AXIS,
    sortNullsLast: true,
  },
  {
    id: "halo_sales",
    name: "Halo Sales",
    color: metricColors.sales[2],
    accessor: (d) => d?.halo_sales,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "gateway_rank",
    name: "Gateway Rank",
    color: metricColors.metric2[1],
    accessor: (d) => d?.gateway_rank,
    format: "number",
    axis: BSR_AXIS,
    sortNullsLast: true,
  },
  {
    id: "search_query_score",
    name: "Search Query Score",
    color: metricColors.metric2[1],
    accessor: (d) => d?.search_query_score,
    format: "number",
    axis: SCORE_AXIS,
    toolTip: "Indicates the popularity and relevance of a search term to your brand or products",
  },
  {
    id: "search_query_score_prev",
    name: "Search Query Score Previous",
    color: metricColors.metric2[2],
    accessor: (d) => d?.search_query_score_prev,
    format: "number",
    axis: SCORE_AXIS,
  },
  {
    id: "search_query_volume",
    name: "Search Query Volume",
    color: metricColors.metric2[1],
    accessor: (d) => d?.search_query_volume,
    format: "number",
    axis: VOLUME_AXIS,
    toolTip: "The search frequency of a term",
  },
  {
    id: "search_query_volume_percent",
    name: "Search Query Volume Change %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.search_query_volume_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "impressions_brand_or_asin",
    name: "Impressions: Brand Count",
    color: metricColors.metric1[1],
    accessor: (d) => d?.impressions_brand_or_asin,
    format: "number",
    chartType: "column",
    axis: IMPRESSIONS_AXIS,
    toolTip: "The number of times your brand or ASIN was shown in search results",
  },
  {
    id: "impressions_total",
    name: "Impressions: Total Count",
    color: metricColors.metric1[3],
    accessor: (d) => d?.impressions_total,
    format: "number",
    chartType: "column",
    axis: IMPRESSIONS_AXIS,
  },
  {
    id: "impressions_total_percent",
    name: "Impressions: Total %",
    color: metricColors.metric1[1],
    accessor: (d) => d?.impressions_total_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "impressions_brand_or_asin_percent",
    name: "Impressions: Brand Share %",
    color: metricColors.metric1[1],
    accessor: (d) => d?.impressions_brand_or_asin_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "impressions_brand_or_asin_share",
    name: "Impressions: Brand Share %",
    color: metricColors.metric1[1],
    accessor: (d) => d?.impressions_brand_or_asin_share,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of impressions attributed to your brand or ASIN",
  },
  {
    id: "impressions_brand_or_asin_share_delta",
    name: "Impressions: Brand Share Change %",
    color: metricColors.metric1[1],
    accessor: (d) => d?.impressions_brand_or_asin_share_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_total",
    name: "Clicks: Total Count",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_total,
    format: "number",
    chartType: "column",
    axis: CLICK_AXIS,
    toolTip: "The total number of clicks on your ads",
  },
  {
    id: "clicks_total_percent",
    name: "Clicks: Total Count %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_total_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_total_rate",
    name: "Clicks: Click Rate %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_total_rate,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of impressions that resulted in a click",
  },
  {
    id: "clicks_total_rate_delta",
    name: "Clicks: Click Rate Change %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_total_rate_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_brand_or_asin",
    name: "Clicks: Brand Count",
    color: metricColors.metric2[3],
    accessor: (d) => d?.clicks_brand_or_asin,
    format: "number",
    chartType: "column",
    axis: CLICK_AXIS,
    toolTip: "The number of clicks on ads for your brand or ASIN",
  },
  {
    id: "clicks_brand_or_asin_percent",
    name: "Clicks: Brand Count % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_brand_or_asin_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_brand_or_asin_share",
    name: "Clicks: Brand Share %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_brand_or_asin_share,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    toolTip: "The percentage of clicks attributed to your brand or ASIN",
  },
  {
    id: "clicks_brand_or_asin_share_delta",
    name: "Clicks: Brand Share Change %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_brand_or_asin_share_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_price_median",
    name: "Clicks: Price (Median)",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_price_median,
    format: "currency",
    axis: PRICE_AXIS,
    toolTip: "The median price of products clicked in ads",
  },
  {
    id: "clicks_price_median_percent",
    name: "Clicks: Price (Median) % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_price_median_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_brand_or_asin_price_median",
    name: "Clicks: Brand Price (Median)",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_brand_or_asin_price_median,
    format: "currency",
    axis: PRICE_AXIS,
    toolTip: "The median price of products clicked in ads for your brand or ASIN",
  },
  {
    id: "clicks_brand_or_asin_price_median_percent",
    name: "Clicks: Brand Price (Median) Change %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_brand_or_asin_price_median_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_same_day_shipping_speed",
    name: "Clicks: Same Day Shipping Speed",
    color: metricColors.metric2[0],
    accessor: (d) => d?.clicks_same_day_shipping_speed,
    format: "number",
    chartType: "column",
    axis: CLICK_AXIS,
    toolTip: "The number of clicks on ads with same shipping speed",
  },
  {
    id: "clicks_same_day_shipping_speed_percent",
    name: "Clicks: Same Day Shipping Speed % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_same_day_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_1d_shipping_speed",
    name: "Clicks: 1D Shipping Speed",
    color: metricColors.metric2[2],
    accessor: (d) => d?.clicks_1d_shipping_speed,
    format: "number",
    chartType: "column",
    axis: CLICK_AXIS,
    toolTip: "The number of clicks on ads with 1 day shipping speed",
  },
  {
    id: "clicks_1d_shipping_speed_percent",
    name: "Clicks: 1D Shipping Speed % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_1d_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_2d_shipping_speed",
    name: "Clicks: 2D Shipping Speed",
    color: metricColors.metric2[4],
    accessor: (d) => d?.clicks_2d_shipping_speed,
    format: "number",
    chartType: "column",
    axis: CLICK_AXIS,
    toolTip: "The number of clicks on ads with 2 day shipping speed",
  },
  {
    id: "clicks_2d_shipping_speed_percent",
    name: "Clicks: 2D Shipping Speed % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_2d_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_total",
    name: "Cart Adds: Total Count",
    color: metricColors.metric3[0],
    accessor: (d) => d?.cart_adds_total,
    format: "number",
    chartType: "column",
    axis: CART_ADDS_AXIS,
    toolTip: "The total number of times products were added to the cart from ads",
  },
  {
    id: "cart_adds_total_percent",
    name: "Cart Adds: Total Count % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_total_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_total_rate",
    name: "Cart Adds: Cart Add Rate %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_total_rate,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    toolTip: "The percentage of clicks that resulted in a cart add",
  },
  {
    id: "cart_adds_brand_or_asin",
    name: "Cart Adds: Brand Count",
    color: metricColors.metric3[1],
    accessor: (d) => d?.cart_adds_brand_or_asin,
    format: "number",
    chartType: "column",
    axis: CART_ADDS_AXIS,
    toolTip: "The number of times products from your brand or ASIN were added to the cart from ads",
  },
  {
    id: "cart_adds_brand_or_asin_percent",
    name: "Cart Adds: Brand Count % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_brand_or_asin_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_brand_or_asin_share",
    name: "Cart Adds: Brand Share %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_brand_or_asin_share,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of cart adds attributed to your brand or ASIN",
  },
  {
    id: "cart_adds_price_median",
    name: "Cart Adds: Price (Median)",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_price_median,
    format: "currency",
    axis: PRICE_AXIS,
    toolTip: "The median price of products added to the cart from ads",
  },
  {
    id: "cart_adds_brand_or_asin_price_median",
    name: "Cart Adds: Brand Price (Median)",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_brand_or_asin_price_median,
    format: "currency",
    axis: PRICE_AXIS,
    toolTip: "The median price of products from your brand or ASIN added to the cart from ads",
  },
  {
    id: "cart_adds_same_day_shipping_speed",
    name: "Cart Adds: Same Day Shipping Speed",
    color: metricColors.metric3[2],
    accessor: (d) => d?.cart_adds_same_day_shipping_speed,
    format: "number",
    chartType: "column",
    axis: CART_ADDS_AXIS,
    toolTip: "The number of cart adds for products with same day shipping speed",
  },
  {
    id: "cart_adds_same_day_shipping_speed_percent",
    name: "Cart Adds: Same Day Shipping Speed % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_same_day_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_1d_shipping_speed",
    name: "Cart Adds: 1D Shipping Speed",
    color: metricColors.metric3[3],
    accessor: (d) => d?.cart_adds_1d_shipping_speed,
    format: "number",
    chartType: "column",
    axis: CART_ADDS_AXIS,
    toolTip: "The number of cart adds for products with 1 day shipping speed",
  },
  {
    id: "cart_adds_1d_shipping_speed_percent",
    name: "Cart Adds: 1D Shipping Speed % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_1d_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_2d_shipping_speed",
    name: "Cart Adds: 2D Shipping Speed",
    color: metricColors.metric3[4],
    accessor: (d) => d?.cart_adds_2d_shipping_speed,
    format: "number",
    chartType: "column",
    axis: CART_ADDS_AXIS,
    toolTip: "The number of cart adds for products with 2 day shipping speed",
  },
  {
    id: "cart_adds_2d_shipping_speed_percent",
    name: "Cart Adds: 2D Shipping Speed % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_2d_shipping_speed_percent,
    format: "percent",
  },
  {
    id: "purchases_total",
    name: "Purchases: Total Count",
    color: metricColors.metric4[0],
    accessor: (d) => d?.purchases_total,
    format: "number",
    chartType: "column",
    axis: PURCHASES_AXIS,
    toolTip: "The total number of purchases made through ads",
  },
  {
    id: "purchases_total_percent",
    name: "Purchases: Total Count Change %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_total_percent,
    format: "number",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_total_rate",
    name: "Purchases: Purchase Rate %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_total_rate,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of clicks that resulted in a purchase",
  },
  {
    id: "purchases_total_rate_delta",
    name: "Purchases: Purchase Rate Change %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_total_rate_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_brand_or_asin",
    name: "Purchases: Brand Count",
    color: metricColors.metric4[1],
    accessor: (d) => d?.purchases_brand_or_asin,
    format: "number",
    chartType: "column",
    axis: PURCHASES_AXIS,
    toolTip: "The number of purchases made for products from your brand or ASIN",
  },
  {
    id: "purchases_brand_or_asin_percent",
    name: "Purchases: Brand Count Change %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_brand_or_asin_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_brand_or_asin_share",
    name: "Purchases: Brand Share",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_brand_or_asin_share,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of purchases attributed to your brand or ASIN",
  },
  {
    id: "purchases_brand_or_asin_share_delta",
    name: "Purchases: Brand Share Change %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_brand_or_asin_share_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_price_median",
    name: "Purchases: Price (Median)",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_price_median,
    format: "currency",
    axis: PRICE_AXIS,
    toolTip: "The median price of products purchased through ads",
  },
  {
    id: "purchases_price_median_percent",
    name: "Purchases: Price (Median) Change %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_price_median_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_brand_or_asin_price_median",
    name: "Purchases: Brand Price (Median)",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_brand_or_asin_price_median,
    format: "currency",
    axis: PRICE_AXIS,
    toolTip: "The median price of products purchased through ads for your brand or ASIN",
  },
  {
    id: "purchases_brand_or_asin_price_median_percent",
    name: "Purchases: Brand Price (Median) Change %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_brand_or_asin_price_median_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_same_day_shipping_speed",
    name: "Purchases: Same Day Shipping Speed",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_same_day_shipping_speed,
    format: "number",
    axis: PURCHASES_AXIS,
    toolTip: "The number of purchases made for products with same day shipping speed",
  },
  {
    id: "purchases_same_day_shipping_speed_percent",
    name: "Purchases: Same Day Shipping Speed % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_same_day_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_1d_shipping_speed",
    name: "Purchases: 1D Shipping Speed",
    color: metricColors.metric4[3],
    accessor: (d) => d?.purchases_1d_shipping_speed,
    format: "number",
    chartType: "column",
    axis: PURCHASES_AXIS,
    toolTip: "The number of purchases made for products with 1 day shipping speed",
  },
  {
    id: "purchases_1d_shipping_speed_percent",
    name: "Purchases: Same Day Shipping Speed % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_1d_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_2d_shipping_speed",
    name: "Purchases: 2D Shipping Speed",
    color: metricColors.metric4[4],
    accessor: (d) => d?.purchases_2d_shipping_speed,
    format: "number",
    chartType: "column",
    axis: PURCHASES_AXIS,
    toolTip: "The number of purchases made for products with 2 day shipping speed",
  },
  {
    id: "purchases_2d_shipping_speed_percent",
    name: "Purchases: Same Day Shipping Speed % Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_2d_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "ctr_total",
    name: "CTR Average",
    color: metricColors.metric2[1],
    accessor: (d) => d?.ctr_total,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    toolTip: "The average click-through rate across all ads",
  },
  {
    id: "ctr_total_delta",
    name: "CTR Average Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.ctr_total_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    toolTip: "The change in click-through rate compared to the previous period",
  },
  {
    id: "ctr_brand_or_asin",
    name: "CTR: Brand",
    color: metricColors.metric2[1],
    accessor: (d) => d?.ctr_brand_or_asin,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    toolTip: "The click-through rate for ads related to your brand or ASIN",
  },
  {
    id: "ctr_brand_or_asin_delta",
    name: "CTR: Brand Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.ctr_brand_or_asin_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "ctr_difference",
    name: "CTR: Difference",
    color: metricColors.metric2[1],
    accessor: (d) => d?.ctr_difference,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "The difference in click-through rate between your brand or ASIN and the average",
    decimalPlaces: 2,
  },
  {
    id: "cv_total",
    name: "Conversion Rate: Average",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cv_total,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    toolTip: "The average conversion rate across all ads",
  },
  {
    id: "cv_total_delta",
    name: "Conversion Rate: Average Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cv_total_delta,
    format: "percent",
    decimalPlaces: 2,
    axis: PERCENT_AXIS,
  },
  {
    id: "cv_brand_or_asin",
    name: "Conversion Rate: Brand",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cv_brand_or_asin,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    toolTip: "The conversion rate for ads related to your brand or ASIN",
  },
  {
    id: "cv_brand_or_asin_delta",
    name: "Conversion Rate: Brand Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cv_brand_or_asin_delta,
    format: "percent",
    decimalPlaces: 2,
    axis: PERCENT_AXIS,
  },
  {
    id: "cv_difference",
    name: "Conversion Rate: Difference",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cv_difference,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "The difference in conversion rate between your brand or ASIN and the average",
    decimalPlaces: 2,
  },
];

export const getFormatter = (
  marketPlace: { marketPlace?: string } | undefined | null,
  format: string,
  metric?: { decimalPlaces?: number; incremental?: boolean; compact?: boolean; clipDecimalPlaces?: boolean },
  options?: { overrideFormatter?: (v: unknown) => string; incremental?: boolean },
): ((v: unknown) => ReactNode) => {
  const defaultFormatter = (v: unknown) => (v == null ? "-" : (v as string));
  const decimalPlaces = metric?.clipDecimalPlaces ? 0 : (metric?.decimalPlaces ?? 1);

  let compact = false;
  if ((format === "currency" && metric?.compact !== false) || metric?.compact) {
    compact = true;
  }

  const formatter =
    {
      currency: (v: unknown) =>
        v == null ? "-" : (
          formatCurrency(v as number, marketPlace?.marketPlace, metric?.clipDecimalPlaces ?? false, false, compact)
        ),
      number: (v: unknown) =>
        v == null || isNaN(v as number) ? "-" : formatNumber(v as number, { decimalPlaces, compact }),
      percent: (v: unknown) => (v == null || isNaN(v as number) ? "-" : formatPercent(v as number, decimalPlaces)),
      text: (v: unknown) => {
        return v == null ? "-" : (v as string);
      },
    }[format] ?? defaultFormatter;

  if (options?.overrideFormatter) {
    return (v: unknown) => options.overrideFormatter?.(v) ?? "-";
  }
  if (options?.incremental ?? metric?.incremental) {
    return (v: unknown) => ((v as number) > 0 ? "+" : "") + formatter(v);
  }
  return formatter;
};

const getMetricSpec = (marketPlace: { marketPlace?: string }, id: string, options?: Partial<MetricConfig>) => {
  const metric = [...metrics, ...dspMetrics].find((m) => m.id === id) || options;

  const formatter = getFormatter(marketPlace, metric?.format ?? "number", metric, options);

  return {
    ...metric,
    formatter,
    value: undefined as unknown,
    ...options,
  };
};

const buildMetric = <T,>(
  marketPlace: { marketPlace?: string },
  data: { total?: T } | null | undefined,
  id: string,
  compareId?: string,
  options?: {
    metric?: Partial<MetricConfig<T>>;

    // All these "override" options are only used in `src/views/merchandise/LaunchDetails.jsx`, however the logic is non-trivial, so not cleaning up now.
    override?: boolean;
    overrideValue?: number | null;
    overrideLabel?: string;
    overrideTooltip?: string;
    overrideTarget?: boolean;
    overrideTargetValue?: number;
    overrideTargetLabel?: string;
    overrideTargetFormatter?: (v: unknown) => ReactNode;
    overrideTargetLower?: boolean;
    overrideMarketplace?: string;
  },
) => {
  const metric = getMetricSpec(marketPlace, id, options?.metric);
  const compare = compareId ? getMetricSpec(marketPlace, compareId) : null;

  if (metric.totalOverrideAccessor) {
    const totalMetric = getMetricSpec(marketPlace, metric.totalOverrideAccessor);

    if (totalMetric.accessor && typeof totalMetric.accessor === "function") {
      metric.overrideValue = totalMetric.accessor(data?.total);
    }
  }
  metric.grouping = METRIC_GROUPING_LOOKUP[id];
  metric.actual = metric.accessor?.(data?.total);
  metric.target = compare ? compare.accessor?.(data?.total) : null;
  metric.targetFormatter = compare ? compare.formatter : undefined;
  metric.overrideMarketplace = options?.overrideMarketplace;
  if (options?.override) {
    metric.actual = options.overrideValue ?? metric.actual;
    metric.name = options.overrideLabel ?? metric.name;
    metric.toolTip = options.overrideTooltip ?? metric.toolTip;
  }
  if (options?.overrideTarget) {
    metric.targetLabel = options.overrideTargetLabel;
    metric.target = options.overrideTargetValue;
    metric.targetFormatter = options.overrideTargetFormatter;
    metric.lower = options.overrideTargetLower;
  }
  if (!metric.format) {
    return metric;
  }

  const decimalPlaces = metric.clipDecimalPlaces ? 0 : (metric.decimalPlaces ?? 1);

  let compact = false;
  if ((metric.format === "currency" && metric.compact !== false) || metric.compact) {
    compact = true;
  }

  metric.formatter = getFormatter(marketPlace, metric.format, {
    ...metric,
    decimalPlaces: decimalPlaces,
    compact: compact,
  });

  metric.compareFormatOptions = {
    format: compare?.format as "number" | "percent" | "currency",
    decimalPlaces: compare?.decimalPlaces,
    compact: compare?.compact,
  };

  return metric;
};

export default getMetricSpec;
export { buildMetric };
